import React, { useEffect, useState, useCallback } from 'react';
import {
  Plus,
  Trash,
  FolderOpen,
  FolderClosed,
  ChevronDown,
  ChevronRight,
  Folders,
  HelpCircle,
} from 'lucide-react';
import ConditionsField from 'components/shared/ConditionFields/ConditionFields';
import { CONDITION_OPTIONS } from 'constants/segmentConstants';
import { useSelector } from 'react-redux';
import {
  selectSegments,
  selectSegmentsLoading,
} from 'store/segments/segments.selector';
import { Condition, ISegmentForm } from 'types/segments/segments.types';
import { validateConditions } from 'utils/conditionValidation';

const SegmentsCondition = ({
  segment,
  setSegment,
}: {
  segment: ISegmentForm;
  setSegment: (segment: ISegmentForm) => void;
}) => {
  const conditions = segment.conditions;
  const [conditionType, setConditionType] = useState('and');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const segments = useSelector(selectSegments);
  const segmentsLoading = useSelector(selectSegmentsLoading);

  useEffect(() => {
    setConditionType(Object.keys(conditions || {})[0] || 'and');
  }, [conditions]);

  // Segment options for Field when data source is "segment"
  const segmentFieldOptions = segmentsLoading
    ? [{ label: 'Loading segments...', value: '', tag: '' }]
    : (segments || [])
        .filter((s) => s && s.name && s._id)
        .map((s) => ({
          label: s.name,
          value: s._id || '',
          tag: s.type || '',
        }));

  const validateAndUpdateErrors = useCallback(() => {
    validateConditions(segment.conditions || {});
  }, [segment.conditions]);

  useEffect(() => {
    validateAndUpdateErrors();
  }, [validateAndUpdateErrors, segment]);

  const updateSegmentConditions = (
    updater: (conditions: Condition[]) => Condition[]
  ) => {
    const newConditions = {
      ...segment.conditions,
      [conditionType]: updater(segment.conditions[conditionType] || []),
    };

    setSegment({
      ...segment,
      conditions: newConditions,
    });
  };

  const isGroupCondition = (condition: Condition): boolean => {
    return condition.condition === 'group';
  };

  const generateGroupId = (
    index: number,
    level: number = 0,
    parentPath: string = ''
  ) => {
    return parentPath
      ? `${parentPath}-group-${index}`
      : `group-${level}-${index}`;
  };

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const renderCondition = (
    condition: Condition,
    index: number,
    level: number = 0,
    parentPath: string = '',
    updateParentCondition?: (
      updater: (condition: Condition) => Condition
    ) => void,
    arrayCondition?: string,
    groupIndex?: number
  ) => {
    const { value } = condition;

    const updateCondition = (updater: (condition: Condition) => Condition) => {
      if (updateParentCondition) {
        updateParentCondition(updater);
      } else {
        updateSegmentConditions((prevConditions) => {
          const updatedCondition = updater(prevConditions[index]);
          if (updatedCondition && (updatedCondition as any).shouldDelete) {
            return prevConditions.filter((_, i) => i !== index);
          }
          const newConditions = [...prevConditions];
          newConditions[index] = updatedCondition;
          return newConditions;
        });
      }
    };

    if (isGroupCondition(condition)) {
      const groupId = generateGroupId(index, level, parentPath);
      const isExpanded = expandedGroups.has(groupId);
      const groupType = (value as any)?.groupType || 'and';
      const groupConditions = (value as any)?.conditions || [];

      const updateGroup = (updater: (groupData: any) => any) => {
        updateCondition((prev) => ({
          ...prev,
          value: updater(prev.value || {}),
        }));
      };

      const addConditionToGroup = () => {
        updateGroup((prev) => ({
          ...prev,
          conditions: [
            ...(prev.conditions || []),
            {
              condition: 'attribute',
              field: '',
              operator: '',
              value: null,
            },
          ],
        }));
      };

      const addNestedGroupToGroup = () => {
        updateGroup((prev) => ({
          ...prev,
          conditions: [
            ...(prev.conditions || []),
            {
              condition: 'group',
              field: '',
              operator: '',
              value: {
                groupType: 'and',
                conditions: [],
              },
            },
          ],
        }));
      };

      const updateGroupCondition = (
        conditionIndex: number,
        updater: (condition: Condition) => Condition
      ) => {
        updateGroup((prev) => {
          const updatedCondition = updater(prev.conditions[conditionIndex]);
          if (updatedCondition && (updatedCondition as any).shouldDelete) {
            return {
              ...prev,
              conditions: (prev.conditions || []).filter(
                (_: Condition, i: number) => i !== conditionIndex
              ),
            };
          }
          return {
            ...prev,
            conditions: (prev.conditions || []).map(
              (c: Condition, i: number) =>
                i === conditionIndex ? updatedCondition : c
            ),
          };
        });
      };

      const deleteGroup = () => {
        if (updateParentCondition) {
          updateParentCondition(() => ({ shouldDelete: true }) as any);
        } else {
          updateSegmentConditions((prevConditions) => {
            const newConditions = [...prevConditions];
            newConditions.splice(index, 1);
            return newConditions;
          });
        }
      };

      return (
        <div
          key={groupId}
          className={`w-full border-2 rounded-lg ${level === 0 ? 'border-primary' : 'border-condition-nested'}`}
        >
          <div
            className={`flex items-center justify-between p-3 ${isExpanded ? 'border-b border-b-2' : ''} ${level === 0 ? 'bg-primary/10 border-primary' : 'bg-condition-foreground border-condition-nested'}`}
          >
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggleGroup(groupId)}
                className={`p-1 rounded ${level === 0 ? 'hover:bg-primary/20' : 'hover:bg-condition-nested/20'}`}
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {isExpanded ? (
                <FolderOpen
                  className={`w-4 h-4 ${level === 0 ? 'text-primary' : 'text-condition-nested'}`}
                />
              ) : (
                <FolderClosed
                  className={`w-4 h-4 ${level === 0 ? 'text-primary' : 'text-condition-nested'}`}
                />
              )}
              <select
                aria-label="Group match type"
                className="text-sm font-medium bg-transparent border-none outline-none"
                value={groupType}
                onChange={(e) => {
                  updateGroup((prev) => ({
                    ...prev,
                    groupType: e.target.value,
                  }));
                }}
              >
                <option value="and">All</option>
                <option value="or">Any</option>
              </select>
              <span className="text-sm text-gray-600">
                of the following conditions match
              </span>
              {level > 0 && (
                <span className="text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">
                  Nested Group
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={addConditionToGroup}
                className={`p-1 rounded ${level === 0 ? 'text-primary hover:bg-primary/20' : 'text-condition-nested hover:bg-condition-nested/20'}`}
                title="Add Condition"
              >
                <Plus className="w-4 h-4" />
              </button>
              {/* Only allow adding nested groups at level 0 */}
              {level === 0 && (
                <button
                  onClick={addNestedGroupToGroup}
                  className="p-1 text-primary hover:bg-primary/20 rounded"
                  title="Add Nested Group"
                >
                  <Folders className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={deleteGroup}
                className="p-1 text-destructive hover:bg-destructive/20 rounded"
                title="Delete Group"
              >
                <Trash className="w-4 h-4 text-destructive" />
              </button>
            </div>
          </div>

          {/* Group Content */}
          {isExpanded && (
            <div className="p-4 space-y-3">
              {groupConditions.map(
                (groupCondition: Condition, conditionIndex: number) => {
                  return (
                    <React.Fragment
                      key={`${groupId}-condition-${conditionIndex}`}
                    >
                      {renderCondition(
                        groupCondition,
                        conditionIndex,
                        level + 1,
                        groupId,
                        (updater) =>
                          updateGroupCondition(conditionIndex, updater),
                        undefined,
                        index
                      )}
                      {conditionIndex < groupConditions.length - 1 && (
                        <div className="flex items-center w-full my-2">
                          <div className="flex-1 h-px bg-gray-300"></div>
                          <div className="px-4 py-1.5 mx-3 bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wide rounded-full shadow-sm">
                            {groupType === 'and' ? 'And' : 'Or'}
                          </div>
                          <div className="flex-1 h-px bg-gray-300"></div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                }
              )}

              {groupConditions.length === 0 && (
                <div className="text-center text-gray-500 text-sm py-4">
                  No conditions in this group. Add conditions to get started.
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <span
        key={`${parentPath}-condition-${index}`}
        className={`w-full flex justify-between text-black text-sm  rounded-lg  duration-200
                ${arrayCondition ? 'ml-4' : ' bg-condition-background px-4 py-4 border border-gray-300 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition-all'}
            `}
      >
        <ConditionsField
          condition={condition}
          setCondition={(c) => updateCondition(() => c)}
          dataSourceOptions={CONDITION_OPTIONS}
          segmentOptions={segmentFieldOptions}
        />

        {/* Delete Button */}
        {!arrayCondition && (
          <div className="flex justify-end">
            <button
              onClick={() => {
                if (updateParentCondition) {
                  // This is a nested condition, signal parent to remove it
                  updateParentCondition(() => ({ shouldDelete: true }) as any);
                } else {
                  // This is a root level condition
                  updateSegmentConditions((prevConditions) => {
                    const newConditions = [...prevConditions];
                    newConditions.splice(index, 1);
                    return newConditions;
                  });
                }
              }}
              className="p-2 text-destructive hover:text-destructive/80 hover:bg-destructive/10 rounded-lg transition-colors"
            >
              <Trash className="w-4 h-4" />
            </button>
          </div>
        )}
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-6 px-4 md:px-6 pt-4 md:pt-6 bg-slate-50 rounded-md border-2 border-primary">
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-700 font-medium">Match</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setSegment({
                ...segment,
                conditions: {
                  and: segment.conditions[conditionType] || [],
                },
              });
              setConditionType('and');
            }}
            className={`px-4 py-1.5 rounded-lg text-sm font-bold uppercase transition-all ${
              conditionType === 'and'
                ? 'bg-primary text-primary-foreground'
                : 'bg-condition-foreground text-gray-700 border border-gray-300'
            }`}
          >
            AND
          </button>
          <button
            type="button"
            onClick={() => {
              setSegment({
                ...segment,
                conditions: {
                  or: segment.conditions[conditionType] || [],
                },
              });
              setConditionType('or');
            }}
            className={`px-4 py-1.5 rounded-lg text-sm font-bold uppercase transition-all ${
              conditionType === 'or'
                ? 'bg-primary text-primary-foreground'
                : 'bg-condition-foreground text-gray-700 border border-gray-300'
            }`}
          >
            OR
          </button>
        </div>
        <button
          type="button"
          className="w-5 h-5 rounded-full border border-gray-300 bg-white flex items-center justify-center hover:bg-gray-50 transition-colors"
          title="Help"
        >
          <HelpCircle className="w-3.5 h-3.5 text-gray-600" />
        </button>
      </div>

      <div className="flex flex-col gap-4 w-full">
        {Object.values(conditions || {})
          .flat()
          .map((condition, index) => {
            return (
              <React.Fragment key={`root-condition-${index}`}>
                {renderCondition(condition, index, 0)}
                {index < Object.values(conditions).flat().length - 1 && (
                  <div className="flex items-center w-full my-2">
                    <div className="flex-1 h-px bg-gray-300"></div>
                    <div className="px-4 py-1.5 mx-3 bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wide rounded-full shadow-sm">
                      {conditionType === 'and' ? 'And' : 'Or'}
                    </div>
                    <div className="flex-1 h-px bg-gray-300"></div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pb-6">
        <button
          className="bg-white flex items-center gap-2 text-gray-700 border border-gray-300 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400 text-sm px-4 py-2 rounded-lg shadow-sm transition-all duration-200"
          onClick={() => {
            updateSegmentConditions((prevConditions) => [
              ...prevConditions,
              {
                condition: 'attribute',
                field: '',
                operator: '',
                value: null,
              },
            ]);
          }}
        >
          <Plus className="w-4 h-4" />
          Add Condition
        </button>

        <button
          className="bg-primary/90 flex items-center gap-2 text-white border border-primary hover:bg-primary hover:border-primary text-sm px-4 py-2 rounded-lg shadow-sm transition-all duration-200"
          onClick={() => {
            updateSegmentConditions((prevConditions) => [
              ...prevConditions,
              {
                condition: 'group',
                field: '',
                operator: '',
                value: {
                  groupType: 'and',
                  conditions: [],
                },
              },
            ]);
          }}
        >
          <FolderOpen className="w-4 h-4" />
          Add Group
        </button>
      </div>
    </div>
  );
};

export default SegmentsCondition;
